import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { HiOutlineInformationCircle } from "react-icons/hi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { getFilingDetails, updateStampPaperFromVendor } from "../../api/discussion";
import { getAppSettings } from "../../api/appSettings";

const LABEL_STYLE =
  "text-xs font-normal leading-5 flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;
const INPUT_BASE =
  "box-border flex flex-row items-center justify-between gap-2.5 px-[10px] py-2 h-9 w-full min-h-[36px] bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222]";
const TEXT_MUTED = "text-xs font-normal leading-5 text-[#6D6D6D]";

type StampPaperFromVendorViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

/**
 * Stamp Paper from Vendor — Amount Payable and Payment Status
 * Figma node: 97-3926
 * Layout: gap 22px outer, 20px between rows; two fields side-by-side (gap 20px)
 */
export default function StampPaperFromVendorView({
  leadId,
  onSuccess,
}: StampPaperFromVendorViewProps) {
  const [amount, setAmount] = useState(0);
  const [paidToVendor, setPaidToVendor] = useState<boolean | null>(null);
  const [stampPapersUaTotal, setStampPapersUaTotal] = useState<number | null>(null);
  const [stampPapersPoaTotal, setStampPapersPoaTotal] = useState<number | null>(null);
  const [rateUa, setRateUa] = useState<number | null>(null);
  const [ratePoA, setRatePoA] = useState<number | null>(null);

  const [calcTooltipOpen, setCalcTooltipOpen] = useState(false);
  const calcTooltipRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(!!leadId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!calcTooltipOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        calcTooltipRef.current &&
        !calcTooltipRef.current.contains(e.target as Node)
      ) {
        setCalcTooltipOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [calcTooltipOpen]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([getFilingDetails(leadId), getAppSettings()])
      .then(([res, settings]) => {
        if (cancelled) return;
        const fd = res.filingDetails;
        setAmount(fd.amountPayableToStampVendor ?? 0);
        setPaidToVendor(fd.paidToVendor ?? null);
        setStampPapersUaTotal(fd.stampPapersUaTotal ?? null);
        setStampPapersPoaTotal(fd.stampPapersPoaTotal ?? null);
        setRateUa(settings.stampPaperRateUa ?? null);
        setRatePoA(settings.stampPaperRatePoA ?? null);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load filing details");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const handlePaidToVendorChange = (value: string): void => {
    if (value === "__none__") {
      setPaidToVendor(null);
      return;
    }
    setPaidToVendor(value === "yes");
  };

  const paidToVendorSelectValue =
    paidToVendor === true ? "yes" : paidToVendor === false ? "no" : "__none__";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    setSaving(true);
    try {
      await updateStampPaperFromVendor(leadId, {
        amountPayableToStampVendor: amount,
        paidToVendor: paidToVendor ?? undefined,
      });
      const res = await getFilingDetails(leadId);
      setAmount(res.filingDetails.amountPayableToStampVendor ?? 0);
      setPaidToVendor(res.filingDetails.paidToVendor ?? null);
      setStampPapersUaTotal(res.filingDetails.stampPapersUaTotal ?? null);
      setStampPapersPoaTotal(res.filingDetails.stampPapersPoaTotal ?? null);
      toast.success("Stamp paper from vendor saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading filing details...</p>
      </div>
    );
  }

  return (
    <form
      id="stamp-paper-from-vendor-form"
      onSubmit={handleSubmit}
      className="flex flex-col w-full max-w-[1085px]"
      style={{
        fontFamily: "Inter, sans-serif",
        gap: 22,
      }}
    >
      {/* Row: Amount Payable to vendor | Payment Status */}
      <div
        className="flex flex-col w-full"
        style={{ gap: 20 }}
      >
        <div
          className="flex flex-row items-center w-full flex-wrap"
          style={{ gap: 20 }}
        >
          {/* Amount Payable to vendor (Auto calc) */}
          <div
            className="flex flex-col flex-1 min-w-0"
            style={{ gap: 4, width: 532.5, maxWidth: "100%", minHeight: 60 }}
          >
            <div className="flex items-center gap-1">
              <label
                className={LABEL_STYLE}
                style={{ ...LABEL_FEATURES, color: "#222222" }}
              >
                Amount Payable to vendor (Auto calc)
              </label>
              <div className="relative" ref={calcTooltipRef}>
                <button
                  type="button"
                  onClick={() => setCalcTooltipOpen((o) => !o)}
                  className="flex items-center justify-center text-[#6D6D6D] hover:text-[#222222]"
                  aria-label="Show calculation"
                >
                  <HiOutlineInformationCircle
                    className="w-4 h-4"
                    strokeWidth={1.5}
                  />
                </button>
                {calcTooltipOpen &&
                  (() => {
                    const ua = stampPapersUaTotal ?? 0;
                    const poa = stampPapersPoaTotal ?? 0;
                    const rUa = rateUa ?? 0;
                    const rPoA = ratePoA ?? 0;
                    const uaSubtotal = ua * rUa;
                    const poaSubtotal = poa * rPoA;
                    const total = uaSubtotal + poaSubtotal;
                    return (
                      <div
                        className="absolute left-0 top-full mt-1 z-30 w-72 rounded-[8px] border border-[#EEEEEE] bg-white shadow-lg p-3 text-[12px] leading-5 text-[#222222]"
                        style={{ fontFamily: "Inter, sans-serif" }}
                      >
                        <p className="text-xs font-semibold text-[#101111] mb-2">
                          Calculation Breakdown
                        </p>
                        <div className="flex flex-col gap-1">
                          <div className="flex justify-between">
                            <span className="text-[#6D6D6D]">
                              UA Stamps ({ua} × Rs. {rUa.toLocaleString("en-IN")})
                            </span>
                            <span>
                              Rs. {uaSubtotal.toLocaleString("en-IN")}/-
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#6D6D6D]">
                              PoA Stamps ({poa} × Rs.{" "}
                              {rPoA.toLocaleString("en-IN")})
                            </span>
                            <span>
                              Rs. {poaSubtotal.toLocaleString("en-IN")}/-
                            </span>
                          </div>
                          <div className="border-t border-[#222] pt-1 mt-1 flex justify-between font-semibold text-[#101111]">
                            <span>Total</span>
                            <span>Rs. {total.toLocaleString("en-IN")}/-</span>
                          </div>
                          {amount !== total && (
                            <p className="mt-2 text-[11px] text-[#6D6D6D]">
                              Editable override is in use; auto-calc is Rs.{" "}
                              {total.toLocaleString("en-IN")}/-.
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })()}
              </div>
            </div>
            <div
              className={INPUT_BASE}
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              <span className={`shrink-0 ${TEXT_MUTED}`}>Rs.</span>
              <input
                type="number"
                min={0}
                value={amount}
                onChange={(e) => {
                  const v = e.target.value;
                  const n = v === "" ? 0 : parseInt(v, 10);
                  setAmount(!isNaN(n) && n >= 0 ? n : 0);
                }}
                className="flex-1 min-w-0 bg-transparent border-0 text-xs font-normal leading-5 text-[#222222] focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                placeholder="0"
              />
            </div>
          </div>

          {/* 2.2r Paid to Vendor: Yes/No — status line shows Pending Payment when No */}
          <div
            className="flex flex-col flex-1 min-w-0"
            style={{ gap: 4, width: 532.5, maxWidth: "100%", minHeight: 60 }}
          >
            <label
              className={LABEL_STYLE}
              style={{ ...LABEL_FEATURES, color: "#222222" }}
            >
              Paid to Vendor
            </label>
            <Select
              value={paidToVendorSelectValue}
              onValueChange={handlePaidToVendorChange}
            >
              <SelectTrigger className="h-9 min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Select</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
            {paidToVendor === false ? (
              <p
                className="text-xs font-medium leading-5 text-[#222222] mt-0.5"
                style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                role="status"
              >
                Status: Pending Payment
              </p>
            ) : paidToVendor === true ? (
              <p
                className="text-xs font-medium leading-5 text-[#222222] mt-0.5"
                style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
                role="status"
              >
                Status: Paid
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {/* Save & Continue is provided by the parent footer */}
    </form>
  );
}
